import { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { Card, Flex, Form, Input, Select, Typography } from 'antd';

import equal from 'fast-deep-equal';

import { EMAIL_PLACEHOLDER } from '../../../constants/common';
import messages from '../../../constants/messages';

import { useUnsavedChangesPrompt } from '../../../hooks/useUnsavedChangesPrompt';

import { getRoles } from '../../../redux/actions/admin';
import { createNewUser } from '../../../redux/actions/user';

import CancelSaveButtons from '../../../components/CancelSaveButtons';
import { errorToastHandler, warnToastHandler } from '../../../components/action_notifier';
import UnsavedChangesModal from '../../../components/ui/UnsavedChangesModal';

import AppBreadcrumb from '../../ui-components/AppBreadcrumb';

import './styles.scss';

const { Title } = Typography;

const NewUser = ({ history }) => {
  const dispatch = useDispatch();
  const adminState = useSelector(state => state.admin);

  const [form] = Form.useForm();
  const values = Form.useWatch([], form);

  const [email, setEmail] = useState('');
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState({});
  const [submittable, setSubmittable] = useState(false);
  const [role, setRole] = useState({ label: 'Keeper', value: 'keeper' });

  useEffect(() => {
    const initial = form.getFieldsValue(true);
    setInitialFormValues(initial);
  }, []);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false));
  }, [values]);

  const onChangeEmail = e => {
    setEmail(e.target.value);
  };

  const onChangeRole = (value, option) => {
    setRole(option);
  };

  useEffect(() => {
    dispatch(getRoles());
  }, []);

  const onClickAddUser = () => {
    if (!email) {
      errorToastHandler(messages.ADD_USER_EMAIL_REQUIRED);
    } else if (!role.value) {
      errorToastHandler(messages.ADD_USER_ROLE_REQUIRED);
    } else {
      const payload = {
        email: email,
        role: role.value
      };
      dispatch(createNewUser(payload))
        .then(res => {
          if (res.id) {
            history.push('/administration/users');
            setIsFormDirty(false);
          }
        })
        .catch(e => {
          console.error(e);
        });
    }
  };

  const onClickCancel = () => {
    history.push('/administration/users');
  };

  function handleChange() {
    const current = form.getFieldsValue();
    const isSame = equal(current, initialFormValues);

    setIsFormDirty(!isSame);
  }

  const { NavigationPrompt, modalProps, nextLocation, confirmed, triggerUnsavedChangesPrompt } =
    useUnsavedChangesPrompt({
      when: isFormDirty,
      onSaveAndLeave: async () => {
        if (submittable) {
          await onClickAddUser();

          return true;
        } else {
          warnToastHandler(messages.DATA_REQUIRED);
          form?.validateFields();

          return false;
        }
      },
      onDiscardAndLeave: () => {
        setIsFormDirty(false);
        onClickCancel();
      }
    });

  useEffect(() => {
    if (!isFormDirty && nextLocation) {
      history.push(`${nextLocation.pathname}${nextLocation.search}`);
    }
  }, [isFormDirty, nextLocation, history]);

  const handleCancel = useCallback(() => {
    isFormDirty ? triggerUnsavedChangesPrompt() : onClickCancel();
  }, [isFormDirty]);

  return (
    <div className="new-user-page">
      {NavigationPrompt}
      <Flex gap="small" vertical={false} justify="space-between" wrap>
        <Flex vertical={true}>
          <Title level={4}>Add new user</Title>

          <AppBreadcrumb
            items={[
              {
                title: <Link to="/administration/users">List of User</Link>
              }
            ]}
          />
        </Flex>
        <Flex>
          <CancelSaveButtons disabled={!submittable} handleCancel={handleCancel} handleSave={onClickAddUser} />
        </Flex>
      </Flex>

      <Card className="mb-2">
        <Form
          form={form}
          initialValues={{ role }}
          layout="vertical"
          requiredMark={true}
          className="mt-3"
          onValuesChange={handleChange}>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: messages.FIELD_REQUIRED('Email') },
              {
                type: 'email',
                message: messages.FIELD_INVALID('Email')
              }
            ]}>
            <Input value={email} onChange={onChangeEmail} placeholder={EMAIL_PLACEHOLDER} />
          </Form.Item>
          <Form.Item label="Role" name="role" rules={[{ required: true, message: messages.FIELD_REQUIRED('Role') }]}>
            <Select
              className="w-full"
              options={adminState.roles.map(role => ({ label: role.display_name, value: role.name }))}
              onChange={onChangeRole}
            />
          </Form.Item>
        </Form>
      </Card>
      <Flex justify="end">
        <CancelSaveButtons disabled={!submittable} handleCancel={handleCancel} handleSave={onClickAddUser} />
      </Flex>

      <UnsavedChangesModal
        isOpen={modalProps.visible}
        handleCancel={modalProps.onCancel}
        handleConfirm={modalProps.onSave}
        handleDiscard={modalProps.onDiscard}
      />
    </div>
  );
};

export default memo(NewUser);
